import React, { useEffect, useState } from 'react';
import { useTheme } from "../../context/ThemeContext";
import { getThemeClasses } from "../utils/themeClass";
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import slugify from '../../utils/slugify';

import AllGames from '../../data/gameData.json';
import AllCategories from '../../data/category.json';
import CardGrid from '../gamecard/cardGrid';
import LineSeparator from '../bgMadness/line';
import Loader from '../Loader/loader';
import BgMadness from '../bgMadness/bgMadness';
import generatePaginationNumbers from '../utils/generatePaginationNumbers.jsx';
import { Button } from 'react-bootstrap';
import { getFavorites } from '../utils/favorites';

const GAMES_PER_PAGE = 24;

const Games = () => {
  const { category } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const themeClass = getThemeClasses(theme);

  const currentPage = parseInt(searchParams.get("page")) || 1;

  const [filteredGames, setFilteredGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(currentPage);

  const isFavorites = category === 'favorites';

  const categoryDetails = AllCategories.find(cat => slugify(cat.title) === category);
  const categoryName = categoryDetails?.title || (isFavorites ? "Favorite" : "All");
  const descriptionBlock = categoryDetails?.description || `Play free ${categoryName} games online at BrowserArena. No installs. Just click and play instantly.`;

  const contentBlock = categoryDetails?.content || "";
  const faqBlock = categoryDetails?.faq || [];

  useEffect(() => {
    let filtered = [];

    if (isFavorites) {
      const storedFavs = getFavorites() || [];
      filtered = AllGames.data.filter(game => storedFavs.includes(game.id));
    } else {
      filtered = AllGames.data.filter(g => slugify(g.category) === category);
      if (filtered.length === 0) {
        const prefix = category?.substring(0, 3) || "";
        const matchedKey = Object.keys(AllGames).find(key => key.startsWith(prefix));
        if (matchedKey && AllGames[matchedKey]) {
          filtered = AllGames[matchedKey];
        } else {
          filtered = AllGames.data;
        }
      }
    }

    setFilteredGames(filtered);
    setLoading(false);
    setPage(currentPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [category, currentPage]);

  const totalPages = Math.ceil(filteredGames.length / GAMES_PER_PAGE);
  const start = (currentPage - 1) * GAMES_PER_PAGE;
  const paginatedGames = filteredGames.slice(start, start + GAMES_PER_PAGE);

  const handlePageChange = (newPage) => {
    navigate(`?c=${encodeURIComponent(category)}&page=${newPage}`);
    setPage(newPage);
  };

  return (
    <div className={`relative min-h-screen px-4 sm:px-6 lg:px-10 py-8 overflow-hidden ${themeClass.bgColor} ${themeClass.baseText}`}>
      {/* <div className="absolute inset-0 z-0 pointer-events-none">
        <BgMadness />
      </div> */}

      <Helmet>
        <title>{`${categoryName} Games | Free Online ${categoryName} Games | BrowserArena`}</title>
        <meta name="description" content={descriptionBlock} />
        {!isFavorites && (
          <link rel="canonical" href={`https://browserarena.com/games/${slugify(categoryName)}?page=${currentPage}`} />
        )}
      </Helmet>

      <div className="relative z-10 text-center mb-6">
        <h1 className={`text-2xl md:text-3xl font-extrabold uppercase tracking-wide ${themeClass.accentText}`}>
          🎮 {categoryName} Games
        </h1>
        <div className={`mt-3 text-sm md:text-base max-w-2xl mx-auto ${themeClass.overlayText}`}>
          {descriptionBlock}
        </div>
      </div>

      <LineSeparator />

      {loading ? (
        <Loader />
      ) : (
        <>
          {filteredGames.length === 0 ? (
            <div className="text-center text-lg text-red-500 mt-10">No games found.</div>
          ) : (
            <CardGrid allGames={paginatedGames} />
          )}

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="relative z-20 mt-10 flex justify-center items-center gap-4 flex-wrap">
              <Button
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
                className={`px-4 py-2 rounded font-bold disabled:opacity-40 bg-blue-500 text-white hover:bg-blue-600`}
              >
                ⬅ Prev
              </Button>

              {generatePaginationNumbers(page, totalPages).map((item, index) =>
                item === "..." ? (
                  <span key={index} className={`px-2 ${themeClass.overlayText}`}>...</span>
                ) : (
                  <Button
                    key={index}
                    onClick={() => handlePageChange(item)}
                    className={`px-3 py-1 border ${
                      item === page
                        ? "bg-pink-600 text-white border-pink-600"
                        : `${themeClass.overlayBg} ${themeClass.overlayText} ${themeClass.borderColor} ${themeClass.hoverBorder}`
                    }`}
                  >
                    {item}
                  </Button>
                )
              )}

              <Button
                onClick={() => handlePageChange(page + 1)}
                disabled={page === totalPages}
                className={`px-4 py-2 rounded font-bold disabled:opacity-40 bg-blue-500 text-white hover:bg-blue-600`}
              >
                Next ➡
              </Button>
            </div>
          )}

          {/* Category Content & FAQ Section */}
          {(contentBlock || faqBlock.length > 0) && (
            <section className="relative z-10 mt-12 max-w-4xl mx-auto px-4">
              {contentBlock && (
                <article className={`prose prose-invert max-w-none ${themeClass.overlayText}`}>
                  <h2 className="text-3xl font-bold mb-4">{categoryName} - More About This Category</h2>
                  <div dangerouslySetInnerHTML={{ __html: contentBlock }} />
                </article>
              )}

              {faqBlock.length > 0 && (
                <section className="mt-10">
                  <h3 className={`text-2xl font-bold mb-6 ${themeClass.titleColor}`}>Frequently Asked Questions</h3>
                  <div className="space-y-6">
                    {faqBlock.map((faq, idx) => (
                      <div key={idx} className={`p-5 rounded-lg border ${themeClass.borderColor} ${themeClass.overlayBg}/80`}>
                        <h4 className="font-semibold text-pink-600 mb-2">{faq.question}</h4>
                        <p className={themeClass.overlayText}>{faq.answer}</p>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </section>
          )}
        </>
      )}
    </div>
  );
};

export default Games;
